import { FieldError } from "react-hook-form";
import { View, Text } from "react-native";
import {
  Checkbox,
  CheckboxIcon,
  CheckboxIndicator,
  CheckboxLabel,
} from "ui/gluestack/checkbox";
import {
  AlertCircleIcon,
  CheckIcon,
  CircleIcon,
  Icon,
} from "ui/gluestack/icon";
import clsx from "clsx";

interface Option<T> {
  label: string;
  value: T;
}

interface Props<T> {
  title?: string;
  subtitle?: string;
  options: Option<T>[];
  isMultiSelect?: boolean;
  selectedValues: T[];
  onChange: (value: T[]) => void;
  errorMessage?: FieldError;
  checkboxesInSameRow?: boolean;
}

export default function KeyValueSelector<T extends string | number | null>({
  title,
  subtitle,
  options,
  isMultiSelect,
  selectedValues,
  onChange,
  errorMessage,
  checkboxesInSameRow,
}: Props<T>) {
  const handleChange = (value: T) => {
    if (isMultiSelect) {
      const newSelectedValues = selectedValues.includes(value)
        ? selectedValues.filter((v) => v !== value)
        : [...selectedValues, value];
      onChange(newSelectedValues);
    } else {
      onChange([value]);
    }
  };

  return (
    <View>
      {title && <Text className="font-medium mb-2">{title}</Text>}
      {subtitle && (
        <Text className="text-sm text-gray-600 mb-2">{subtitle}</Text>
      )}

      <View
        className={clsx(
          checkboxesInSameRow ? "flex flex-row gap-x-5" : "gap-y-5",
        )}
      >
        {options.map((option) => (
          <Checkbox
            size="sm"
            key={String(option.value)}
            isChecked={selectedValues.includes(option.value)}
            onChange={() => handleChange(option.value)}
            value={option ? "checked" : "unchecked"}
          >
            <CheckboxIndicator
              className={isMultiSelect ? "rounded" : "rounded-full"}
            >
              <CheckboxIcon as={isMultiSelect ? CheckIcon : CircleIcon} />
            </CheckboxIndicator>
            <CheckboxLabel className="ml-1 capitalize">
              {option.label}
            </CheckboxLabel>
          </Checkbox>
        ))}
      </View>
      {errorMessage ? (
        <View className="flex-row gap-x-1 items-center mt-3">
          <Icon as={AlertCircleIcon} className="text-error-500" size="sm" />
          <Text className="text-error-500 text-sm">{errorMessage.message}</Text>
        </View>
      ) : null}
    </View>
  );
}
